<template>
  <el-card shadow="never" class="email-editor-card">
    <div class="email-editor-header">
      <div class="email-editor-heading">
        <div class="email-editor-title-row">
          <span class="email-editor-title">{{ title }}</span>
          <el-tag size="mini" type="info">
            {{ formatLabel }}
          </el-tag>
        </div>
        <div v-if="editorMetaItems.length" class="email-editor-meta">
          <span
            v-for="item in editorMetaItems"
            :key="item.key"
            class="email-editor-meta-item"
          >
            <i :class="item.icon"></i>
            {{ item.label }}
          </span>
        </div>
      </div>
      <div
        v-if="hasTemplateProperties"
        class="template-property-toolbar"
      >
        <el-dropdown trigger="click">
          <el-button size="small" type="primary" plain>
            <i class="el-icon-collection-tag"></i>
            Variables
            <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown" class="template-property-menu">
            <el-dropdown-item
              v-for="property in templateProperties"
              :key="property.key"
              @click.native="insertTemplateProperty(property.key)"
            >
              <span class="template-property-option">
                <span class="template-property-option-label">
                  {{ property.label || property.key }}
                </span>
                <span class="template-property-option-meta">
                  <el-tag
                    v-if="property.sourceModel"
                    size="mini"
                    type="info"
                  >
                    {{ property.sourceModel }}
                  </el-tag>
                  <span class="template-property-token">
                    {{ getTemplateToken(property.key) }}
                  </span>
                </span>
              </span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
    <div
      v-if="showTemplateAutocomplete"
      class="template-autocomplete-menu"
      :style="templateAutocompleteStyle"
    >
      <button
        v-for="(property, index) in filteredTemplateProperties"
        :key="property.key"
        type="button"
        class="template-autocomplete-item"
        :class="{ 'is-active': index === templateAutocompleteIndex }"
        @mousedown.prevent="selectAutocompleteProperty(property.key)"
      >
        <span>{{ property.label || property.key }}</span>
        <span class="template-property-token">
          {{ getTemplateToken(property.key) }}
        </span>
      </button>
    </div>
    <div style="display:none;">
      <SignaturePreview
        ref="signaturePreview"
        :profile="signatureProfile"
        :first-name="signatureFirstName"
        :last-name="signatureLastName"
        :username="signatureUsername"
        :email="signatureEmail"
        :phone="signaturePhone"
        :title="signatureTitle"
        :department="signatureDepartment"
        :address="signatureAddress"
        :logo-url="logoUrl"
      />
    </div>
    <el-input
      v-if="usesRawEditor"
      ref="mjmlEditor"
      v-model="content"
      class="email-editor-raw"
      type="textarea"
      :rows="20"
      @input="onEditorInput"
    />
    <Editor
      v-else
      ref="emailEditor"
      :id="editorId"
      v-model="editorContent"
      @input="onEditorInput"
      @init="onEditorInit"
      @onInit="onEditorInit"
      :api-key="TINYMCE_API_KEY"
      :init="tinyMceInit"
    />
    <el-card
      v-if="previewDisplayContent && preview && !usesRawEditor"
      shadow="never"
      class="email-editor-preview"
    >
      <div slot="header" class="email-editor-preview-header">
        Preview
      </div>
      <div class="email-editor-preview-body" v-html="previewDisplayContent"></div>
    </el-card>
  </el-card>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import SignaturePreview from './SignaturePreview';
import { TINYMCE_API_KEY } from '../env';
import { getLocationsForPortal } from '../utils/commonApi';
import getParsedTokenTitle from '../utils/keycloak';

const SIGNATURE_MARKER = '<!-- RS_SIGNATURE -->';
const TINYMCE_NONEDITABLE_CLASS = 'mceNonEditable';
const TEMPLATE_VARIABLE_TAG_CLASS = 'managed-template-variable-tag';
const TEMPLATE_TOKEN_PATTERN = /{{{\s*([a-z0-9._-]+)\s*}}}|{{\s*([a-z0-9._-]+)\s*}}/gi;
let editorIdSequence = 0;
const TINYMCE_CONTENT_STYLE = `
  .${TEMPLATE_VARIABLE_TAG_CLASS} {
    display: inline-block;
    margin: 0 2px;
    padding: 0 8px;
    min-height: 20px;
    line-height: 20px;
    border: 1px solid #d9ecff;
    border-radius: 4px;
    background: #ecf5ff;
    color: #409eff;
    font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
    font-size: 12px;
    white-space: nowrap;
    vertical-align: baseline;
  }
`;

function getTemplateTokenValue(key, raw = false) {
  return raw ? `{{{${key}}}}` : `{{${key}}}`;
}

function escapeHtmlValue(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildTemplateVariableTagHtml(key, raw = false) {
  const normalizedKey = String(key || '').trim().toLowerCase();
  const escapedKey = escapeHtmlValue(normalizedKey);

  return [
    `<span class="${TINYMCE_NONEDITABLE_CLASS} ${TEMPLATE_VARIABLE_TAG_CLASS}"`,
    ` data-template-variable="${escapedKey}"`,
    ` data-template-variable-raw="${raw ? 'true' : 'false'}"`,
    ' contenteditable="false"',
    ' data-mce-contenteditable="false">',
    escapeHtmlValue(getTemplateTokenValue(normalizedKey, raw)),
    '</span>',
  ].join('');
}

function stripTemplateVariableTags(value) {
  const html = typeof value === 'string' ? value : '';

  if (!html || typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  Array.from(
    container.querySelectorAll(`span.${TEMPLATE_VARIABLE_TAG_CLASS}[data-template-variable]`),
  ).forEach((node) => {
    const key = node.getAttribute('data-template-variable') || '';
    const raw = node.getAttribute('data-template-variable-raw') === 'true'
      || String(node.textContent || '').trim().startsWith('{{{');
    const token = document.createTextNode(getTemplateTokenValue(key, raw));

    if (node.parentNode) {
      node.parentNode.replaceChild(token, node);
    }
  });

  return container.innerHTML;
}

function decorateTemplateVariables(value) {
  const rawHtml = stripTemplateVariableTags(value);

  if (!rawHtml || typeof document === 'undefined') {
    return rawHtml;
  }

  const container = document.createElement('div');
  container.innerHTML = rawHtml;
  const walker = document.createTreeWalker(container, 4, null, false);
  const textNodes = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    TEMPLATE_TOKEN_PATTERN.lastIndex = 0;

    if (!TEMPLATE_TOKEN_PATTERN.test(text)) {
      return;
    }

    TEMPLATE_TOKEN_PATTERN.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match = TEMPLATE_TOKEN_PATTERN.exec(text);

    while (match) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));

      const key = match[1] || match[2];
      const raw = Boolean(match[1]);
      const wrapper = document.createElement('span');
      wrapper.className = `${TINYMCE_NONEDITABLE_CLASS} ${TEMPLATE_VARIABLE_TAG_CLASS}`;
      wrapper.setAttribute('data-template-variable', key);
      wrapper.setAttribute('data-template-variable-raw', raw ? 'true' : 'false');
      wrapper.setAttribute('contenteditable', 'false');
      wrapper.setAttribute('data-mce-contenteditable', 'false');
      wrapper.textContent = getTemplateTokenValue(key, raw);
      fragment.appendChild(wrapper);

      lastIndex = match.index + match[0].length;
      match = TEMPLATE_TOKEN_PATTERN.exec(text);
    }

    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));

    if (node.parentNode) {
      node.parentNode.replaceChild(fragment, node);
    }
  });

  return container.innerHTML;
}

// import Uploader from './Uploader.vue';
// import { ImageDrop } from 'quill-image-drop-module';
// import ImageResize from 'quill-image-resize-module';

// Quill.register('modules/imageDrop', ImageDrop);
// Quill.register('modules/imageResize', ImageResize);

export default {
  name: 'EmailEditor',
  components: { Editor, SignaturePreview },
  props: {
    value: { type: String, default: '' },
    preview: { type: Boolean, default: false },
    previewContent: { type: String, default: '' },
    from: { type: String, default: null },
    fromLabel: { type: String, default: 'Sender' },
    title: { type: String, default: 'Body' },
    contextLabel: { type: String, default: '' },
    templateProperties: { type: Array, default: () => [] },
    format: { type: String, default: 'html' },
  },
  data() {
    editorIdSequence += 1;

    return {
      TINYMCE_API_KEY,
      editorId: `email-editor-${editorIdSequence}`,
      content: this.value || '',
      editorContent: decorateTemplateVariables(this.value || ''),
      uploadedImage: '',
      editorInstance: null,
      logoUrl: 'https://email-attachments-public.s3.us-east-2.amazonaws.com/rs.png',
      editorConfig: {
        // The configuration of the editor.
      },
      portalLocations: [],
      templateAutocompleteVisible: false,
      templateAutocompleteQuery: '',
      templateAutocompleteIndex: 0,
      templateAutocompletePosition: {
        top: 0,
        left: 0,
      },
      templateAutocompleteMatch: null,
      lastEditorSelectionRange: null,
      boundEditorId: null,
    };
  },
  computed: {
    signatureProfile() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.profile) || '';
    },
    signatureFirstName() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.given_name) || '';
    },
    signatureLastName() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.family_name) || '';
    },
    signatureUsername() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.preferred_username) || '';
    },
    signatureEmail() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.email) || '';
    },
    signaturePhone() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.phone) || '';
    },
    signatureTitle() {
      return getParsedTokenTitle(this.$keycloak);
    },
    signatureDepartment() {
      return (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.department) || '';
    },
    signatureAddress() {
      if (!this.signatureDepartment || this.signatureDepartment === 'None') {
        return '';
      }
      const match = (this.portalLocations || []).find(facility =>
        this.isLocationMatch(facility, this.signatureDepartment));
      if (!match) {
        return '';
      }
      return this.getLocationAddress(match);
    },
    hasTemplateProperties() {
      return Array.isArray(this.templateProperties) && this.templateProperties.length > 0;
    },
    isMjmlFormat() {
      return String(this.format || 'html').toLowerCase() === 'mjml';
    },
    usesRawEditor() {
      return this.isMjmlFormat;
    },
    formatLabel() {
      return this.usesRawEditor ? 'MJML' : 'HTML';
    },
    previewDisplayContent() {
      return this.previewContent || this.content;
    },
    editorMetaItems() {
      const items = [];

      if (this.from) {
        items.push({
          key: 'from',
          icon: 'el-icon-message',
          label: `${this.fromLabel}: ${this.from}`,
        });
      }

      if (this.contextLabel) {
        items.push({
          key: 'context',
          icon: 'el-icon-document',
          label: this.contextLabel,
        });
      }

      if (this.hasTemplateProperties) {
        const variableCount = this.templateProperties.length;
        items.push({
          key: 'variables',
          icon: 'el-icon-collection-tag',
          label: `${variableCount} variable${variableCount === 1 ? '' : 's'}`,
        });
      }

      return items;
    },
    tinyMceInit() {
      return {
        plugins: 'lists link image table code help wordcount noneditable',
        extended_valid_elements: [
          [
            'span[class|data-template-variable|data-template-variable-raw',
            '|contenteditable|data-mce-contenteditable]',
          ].join(''),
        ].join(','),
        noneditable_class: TINYMCE_NONEDITABLE_CLASS,
        custom_ui_selector: '.template-property-toolbar, .el-dropdown-menu, .el-dropdown-menu__item',
        content_style: TINYMCE_CONTENT_STYLE,
      };
    },
    filteredTemplateProperties() {
      const query = String(this.templateAutocompleteQuery || '').toLowerCase();

      return this.templateProperties.filter((property) => {
        if (!query) {
          return true;
        }

        const label = String(property.label || '').toLowerCase();
        const key = String(property.key || '').toLowerCase();
        return label.includes(query) || key.includes(query);
      });
    },
    showTemplateAutocomplete() {
      return this.templateAutocompleteVisible && this.filteredTemplateProperties.length > 0;
    },
    templateAutocompleteStyle() {
      return {
        top: `${this.templateAutocompletePosition.top}px`,
        left: `${this.templateAutocompletePosition.left}px`,
      };
    },
  },
  async mounted() {
    await this.loadLocationsForPortal();
  },
  watch: {
    value(nextValue) {
      const normalizedValue = typeof nextValue === 'string' ? nextValue : '';

      if (normalizedValue === this.content) {
        return;
      }

      const decoratedValue = decorateTemplateVariables(normalizedValue);

      this.content = normalizedValue;
      this.editorContent = decoratedValue;

      if (!this.usesRawEditor && this.editorInstance && typeof this.editorInstance.setContent === 'function') {
        const currentContent = this.editorInstance.getContent();
        if (currentContent !== this.editorContent) {
          this.editorInstance.setContent(this.editorContent || '');
        }
      }
    },
    format() {
      if (this.usesRawEditor) {
        this.editorInstance = null;
        this.closeTemplateAutocomplete();
        return;
      }

      this.$nextTick(() => {
        if (this.editorInstance && typeof this.editorInstance.setContent === 'function') {
          this.editorContent = decorateTemplateVariables(this.content || '');
          this.editorInstance.setContent(this.editorContent || '');
        }
      });
    },
  },
  methods: {
    getLocationValue(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      const { _id: objectId } = facility;
      const facilityId = facility.id || objectId || '';
      return facility.title || facility.name || facility.code || facility.locationCode || facilityId || '';
    },
    isLocationMatch(facility, value) {
      if (!facility || typeof facility !== 'object' || !value) {
        return false;
      }
      const { _id: objectId } = facility;
      const facilityId = facility.id || objectId || '';
      const candidates = [
        facility.title,
        facility.name,
        facility.code,
        facility.locationCode,
        facilityId,
      ];
      return candidates.some(candidate => candidate && String(candidate) === String(value));
    },
    getLocationAddress(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      const addressInfo = facility.addressInfo || {};
      const addressLine1 = addressInfo.addressLine1 || facility.addressLine1 || facility.address || '';
      const addressLine2 = addressInfo.addressLine2 || facility.addressLine2 || facility.address2 || '';
      const city = addressInfo.city || facility.city || '';
      const state = addressInfo.state || facility.state || '';
      const pinCode = addressInfo.pinCode || facility.pinCode || facility.postalCode || '';
      return [addressLine1, addressLine2, city, state, pinCode].filter(Boolean).join(', ');
    },
    getTemplateToken(key) {
      return getTemplateTokenValue(key);
    },
    getTemplateVariableTagHtml(key) {
      return buildTemplateVariableTagHtml(key);
    },
    async loadLocationsForPortal() {
      try {
        const locations = await getLocationsForPortal(this.$keycloak.token);
        this.portalLocations = Array.isArray(locations) ? locations : [];
      } catch (err) {
        this.portalLocations = [];
      }
    },
    onEditorInit(event, editor) {
      const resolvedEditor = editor || (event && event.target) || this.getTinyMceEditor();

      if (!resolvedEditor) {
        return;
      }

      this.editorInstance = resolvedEditor;
      if (this.content) {
        this.editorContent = decorateTemplateVariables(this.content);
        resolvedEditor.setContent(this.editorContent);
      }

      if (this.boundEditorId === resolvedEditor.id) {
        return;
      }

      this.boundEditorId = resolvedEditor.id;
      resolvedEditor.on('keydown', keydownEvent => this.handleEditorKeyDown(keydownEvent));
      resolvedEditor.on('keyup', () => this.handleEditorInteraction());
      resolvedEditor.on('mouseup', () => this.handleEditorInteraction());
      resolvedEditor.on('click', () => this.handleEditorInteraction());
      resolvedEditor.on('focus', () => this.handleEditorInteraction());
      resolvedEditor.on('blur', () => this.closeTemplateAutocomplete());
    },
    getTinyMceEditor() {
      if (this.editorInstance) {
        return this.editorInstance;
      }

      const editorRef = this.$refs.emailEditor;
      const editor = editorRef && editorRef.editor;

      if (editor) {
        this.editorInstance = editor;
        return editor;
      }

      const tinymce = typeof window !== 'undefined' ? window.tinymce : null;
      const editorById = tinymce && typeof tinymce.get === 'function'
        ? tinymce.get(this.editorId)
        : null;

      if (editorById) {
        this.editorInstance = editorById;
        return editorById;
      }

      return null;
    },
    saveEditorSelection() {
      const editor = this.getTinyMceEditor();
      const range = editor && editor.selection && typeof editor.selection.getRng === 'function'
        ? editor.selection.getRng()
        : null;

      if (!range) {
        return;
      }

      this.lastEditorSelectionRange = typeof range.cloneRange === 'function'
        ? range.cloneRange()
        : range;
    },
    restoreEditorSelection(editor) {
      const range = this.lastEditorSelectionRange;

      if (!range || !editor || !editor.selection || typeof editor.selection.setRng !== 'function') {
        return false;
      }

      const body = typeof editor.getBody === 'function' ? editor.getBody() : null;

      if (
        !body ||
        !body.contains(range.startContainer) ||
        !body.contains(range.endContainer)
      ) {
        return false;
      }

      try {
        editor.selection.setRng(range);
        return true;
      } catch (err) {
        return false;
      }
    },
    setEditorCursorInElement(editor, target, options = {}) {
      if (!editor || !editor.selection || !target) {
        return false;
      }

      if (typeof editor.selection.setCursorLocation === 'function') {
        editor.selection.setCursorLocation(
          target,
          options.atEnd ? target.childNodes.length : 0,
        );
        this.saveEditorSelection();
        return true;
      }

      if (
        target.ownerDocument &&
        typeof target.ownerDocument.createRange === 'function' &&
        typeof editor.selection.setRng === 'function'
      ) {
        const range = target.ownerDocument.createRange();
        range.selectNodeContents(target);
        range.collapse(!options.atEnd);
        editor.selection.setRng(range);
        this.saveEditorSelection();
        return true;
      }

      return false;
    },
    insertTemplateVariableIntoEditor(key) {
      const editor = this.getTinyMceEditor();

      if (!editor) {
        return false;
      }

      editor.focus();
      this.restoreEditorSelection(editor);
      const variableHtml = this.getTemplateVariableTagHtml(key);
      let inserted = false;

      if (typeof editor.execCommand === 'function') {
        editor.execCommand('mceInsertContent', false, variableHtml);
        inserted = true;
      } else if (typeof editor.insertContent === 'function') {
        editor.insertContent(variableHtml);
        inserted = true;
      } else if (
        editor.selection &&
        typeof editor.selection.setContent === 'function'
      ) {
        editor.selection.setContent(variableHtml);
        inserted = true;
      }

      if (!inserted) {
        return false;
      }

      this.editorContent = editor.getContent();
      this.onEditorInput(this.editorContent);
      return true;
    },
    getAutocompleteMatch(editor) {
      if (!editor || this.usesRawEditor || !this.hasTemplateProperties) {
        return null;
      }

      const range = editor.selection && editor.selection.getRng
        ? editor.selection.getRng()
        : null;

      if (!range || !range.collapsed) {
        return null;
      }

      let container = range.startContainer;
      let endOffset = range.startOffset;
      let textBeforeCursor = '';

      if (container && container.nodeType === 3) {
        textBeforeCursor = container.data.slice(0, endOffset);
      } else if (container && container.nodeType === 1 && endOffset > 0) {
        const previousNode = container.childNodes[endOffset - 1];
        if (!previousNode || previousNode.nodeType !== 3) {
          return null;
        }

        container = previousNode;
        endOffset = previousNode.data.length;
        textBeforeCursor = previousNode.data;
      } else {
        return null;
      }

      const match = textBeforeCursor.match(/\{\{\{([a-z0-9._-]*)$/i);
      if (!match) {
        return null;
      }

      return {
        node: container,
        startOffset: endOffset - match[0].length,
        endOffset,
        query: match[1] || '',
      };
    },
    getAutocompletePosition(editor) {
      if (!editor || !editor.selection || !editor.selection.getRng) {
        return {
          top: 0,
          left: 0,
        };
      }

      const range = editor.selection.getRng();
      const iframe = editor.getContentAreaContainer
        && editor.getContentAreaContainer()
        && editor.getContentAreaContainer().querySelector('iframe');
      const iframeRect = iframe && iframe.getBoundingClientRect
        ? iframe.getBoundingClientRect()
        : null;
      const rangeRect = range && range.getBoundingClientRect
        ? range.getBoundingClientRect()
        : null;
      const rangeClientRects = range && range.getClientRects ? range.getClientRects() : null;
      const lastRect = rangeClientRects && rangeClientRects.length
        ? rangeClientRects[rangeClientRects.length - 1]
        : rangeRect;

      if (iframeRect && lastRect) {
        return {
          top: iframeRect.top + lastRect.bottom + 8,
          left: iframeRect.left + lastRect.left,
        };
      }

      if (lastRect) {
        return {
          top: lastRect.bottom + 8,
          left: lastRect.left,
        };
      }

      const editorRect = editor.getContainer().getBoundingClientRect();
      return {
        top: editorRect.top + 48,
        left: editorRect.left + 16,
      };
    },
    updateTemplateAutocomplete() {
      const match = this.getAutocompleteMatch(this.editorInstance);

      if (!match) {
        this.closeTemplateAutocomplete();
        return;
      }

      this.templateAutocompleteMatch = match;
      this.templateAutocompleteQuery = match.query;
      this.templateAutocompletePosition = this.getAutocompletePosition(this.editorInstance);
      this.templateAutocompleteVisible = true;

      if (this.templateAutocompleteIndex >= this.filteredTemplateProperties.length) {
        this.templateAutocompleteIndex = 0;
      }
    },
    handleEditorAutocompleteEvent() {
      this.updateTemplateAutocomplete();
    },
    handleEditorInteraction() {
      this.saveEditorSelection();
      this.handleEditorAutocompleteEvent();
    },
    closeTemplateAutocomplete() {
      this.templateAutocompleteVisible = false;
      this.templateAutocompleteQuery = '';
      this.templateAutocompleteIndex = 0;
      this.templateAutocompleteMatch = null;
    },
    selectAutocompleteProperty(key) {
      if (!key) {
        return;
      }

      if (!this.editorInstance || !this.templateAutocompleteMatch) {
        this.insertTemplateProperty(key);
        this.closeTemplateAutocomplete();
        return;
      }

      const currentMatch = this.getAutocompleteMatch(this.editorInstance)
        || this.templateAutocompleteMatch;
      const replacementRange = currentMatch.node
        && currentMatch.node.ownerDocument
        && currentMatch.node.ownerDocument.createRange
        ? currentMatch.node.ownerDocument.createRange()
        : null;

      if (!replacementRange) {
        this.insertTemplateProperty(key);
        this.closeTemplateAutocomplete();
        return;
      }

      this.editorInstance.focus();
      replacementRange.setStart(currentMatch.node, currentMatch.startOffset);
      replacementRange.setEnd(currentMatch.node, currentMatch.endOffset);
      this.editorInstance.selection.setRng(replacementRange);
      if (!this.insertTemplateVariableIntoEditor(key)) {
        this.notifyTemplateVariableInsertFailed();
      }
      this.closeTemplateAutocomplete();
    },
    handleEditorKeyDown(event) {
      if (!this.showTemplateAutocomplete) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.templateAutocompleteIndex = (this.templateAutocompleteIndex + 1)
          % this.filteredTemplateProperties.length;
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.templateAutocompleteIndex = this.templateAutocompleteIndex === 0
          ? this.filteredTemplateProperties.length - 1
          : this.templateAutocompleteIndex - 1;
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const activeProperty = this.filteredTemplateProperties[this.templateAutocompleteIndex];
        if (activeProperty) {
          event.preventDefault();
          this.selectAutocompleteProperty(activeProperty.key);
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeTemplateAutocomplete();
      }
    },
    insertIntoMjmlEditor(token) {
      const textarea = this.$refs.mjmlEditor
        && this.$refs.mjmlEditor.$refs
        && this.$refs.mjmlEditor.$refs.textarea;

      if (!textarea) {
        this.content = `${this.content || ''}${token}`;
        this.onEditorInput(this.content);
        return;
      }

      const value = this.content || '';
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;

      this.content = `${value.slice(0, start)}${token}${value.slice(end)}`;
      this.onEditorInput(this.content);

      this.$nextTick(() => {
        textarea.focus();
        const cursorPosition = start + token.length;
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      });
    },
    insertTemplateProperty(key) {
      if (!key) {
        return;
      }

      const token = this.getTemplateToken(key);

      if (this.usesRawEditor) {
        this.insertIntoMjmlEditor(token);
        return;
      }

      if (this.insertTemplateVariableIntoEditor(key)) {
        this.closeTemplateAutocomplete();
        return;
      }

      this.notifyTemplateVariableInsertFailed();
      this.closeTemplateAutocomplete();
    },
    notifyTemplateVariableInsertFailed() {
      this.$message.error({
        title: 'Unable to insert variable',
        message: 'Place the cursor in the editor and try again.',
      });
    },
    async addedImage(url) {
      this.uploadedImage = url;
      await navigator.clipboard.writeText(url);
      this.$message.success({
        title: 'Url Copied',
      });
    },
    getSignatureHtml() {
      const signature = this.$refs.signaturePreview;
      if (!signature || typeof signature.getRawHtml !== 'function') {
        return '';
      }
      return signature.getRawHtml();
    },
    getCurrentEditorRawContent() {
      if (
        !this.usesRawEditor &&
        this.editorInstance &&
        typeof this.editorInstance.getContent === 'function'
      ) {
        return stripTemplateVariableTags(this.editorInstance.getContent());
      }

      return this.content || '';
    },
    focusEditorContent(options = {}) {
      if (this.usesRawEditor || (!options.cursorSelector && !options.focusAtEnd)) {
        return;
      }

      this.$nextTick(() => {
        const editor = this.getTinyMceEditor();

        if (!editor || !editor.selection || typeof editor.focus !== 'function') {
          return;
        }

        const body = typeof editor.getBody === 'function' ? editor.getBody() : null;

        if (!body) {
          return;
        }

        const target = options.cursorSelector
          ? body.querySelector(options.cursorSelector)
          : null;

        editor.focus();

        try {
          if (target && this.setEditorCursorInElement(editor, target, { atEnd: false })) {
            return;
          }

          if (options.focusAtEnd && typeof editor.selection.select === 'function') {
            editor.selection.select(body, true);
            editor.selection.collapse(false);
            this.saveEditorSelection();
          }
        } catch (err) {
          // TinyMCE may reject a range while the iframe is being refreshed.
        }
      });
    },
    setEditorRawContent(value, options = {}) {
      this.content = typeof value === 'string' ? value : '';

      if (this.usesRawEditor) {
        this.$emit('input', this.content);
        return;
      }

      this.editorContent = decorateTemplateVariables(this.content);

      if (this.editorInstance && typeof this.editorInstance.setContent === 'function') {
        this.editorInstance.setContent(this.editorContent || '');
      }

      this.$emit('input', this.content);
      this.updateTemplateAutocomplete();
      this.focusEditorContent(options);
    },
    insertSignature() {
      const currentContent = this.getCurrentEditorRawContent();

      if (currentContent.includes(SIGNATURE_MARKER)) {
        this.$message.info({
          title: 'Signature already added',
          message: 'Your signature is already in the email.',
        });
        return;
      }
      const signatureHtml = this.getSignatureHtml();
      if (!signatureHtml) {
        this.$message.warning({
          title: 'Signature unavailable',
          message: 'Unable to generate your signature right now.',
        });
        return;
      }

      const spacing = currentContent ? '<br /><br />' : '';
      this.setEditorRawContent(`${currentContent}${spacing}${signatureHtml}`);
    },
    onEditorInput(content) {
      if (this.usesRawEditor) {
        if (typeof content === 'string') {
          this.content = content;
        }
      } else {
        let editorValue = this.editorContent;

        if (typeof content === 'string') {
          editorValue = content;
        } else if (this.editorInstance && typeof this.editorInstance.getContent === 'function') {
          editorValue = this.editorInstance.getContent();
        }

        this.editorContent = editorValue;
        this.content = stripTemplateVariableTags(editorValue);
      }

      this.$emit('input', this.content);

      if (!this.usesRawEditor) {
        this.updateTemplateAutocomplete();
      }
    },
  },
};
</script>

<style>
.tox.tox-silver-sink.tox-tinymce-aux {
  z-index: 9999;
}

.email-editor-card {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
}

.email-editor-card .el-card__body {
  padding: 0;
}

.email-editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;
}

.email-editor-heading {
  min-width: 0;
}

.email-editor-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.email-editor-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.email-editor-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 6px;
}

.email-editor-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 360px;
  font-size: 12px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.email-editor-meta-item i {
  color: #909399;
}

.template-property-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
}

.template-property-toolbar .el-button [class^="el-icon-"] {
  margin-right: 4px;
}

.template-property-menu {
  min-width: 320px;
}

.template-property-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.template-property-option-label {
  min-width: 0;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-property-option-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.template-property-token {
  color: #909399;
  font-family: monospace;
  font-size: 12px;
}

.template-autocomplete-menu {
  position: fixed;
  z-index: 10000;
  min-width: 280px;
  max-width: 360px;
  max-height: 240px;
  overflow: auto;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.template-autocomplete-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #fff;
  border: 0;
  text-align: left;
  cursor: pointer;
}

.template-autocomplete-item:hover,
.template-autocomplete-item.is-active {
  background: #f5f7fa;
}

.email-editor-raw .el-textarea__inner {
  border: 0;
  border-radius: 0;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
}

.email-editor-card .tox-tinymce {
  border: 0;
  border-radius: 0;
}

.email-editor-preview {
  margin: 14px 16px 16px;
  border-radius: 6px;
}

.email-editor-preview .el-card__body {
  padding: 16px;
}

.email-editor-preview-header {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.email-editor-preview-body {
  color: #303133;
}

@media (max-width: 768px) {
  .email-editor-header {
    flex-direction: column;
    align-items: stretch;
  }

  .template-property-toolbar {
    justify-content: flex-start;
  }

  .email-editor-meta-item {
    max-width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/EmailEditor.vue